import * as Sentry from "@sentry/react";
import React, { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from 'react-helmet-async';

import "./index.css";
import './firebaseConfig';
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { initErrorReporting } from './services/errorReporting';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// Disable automatic scroll restoration to ensure each reload starts at top
if (window.history && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

// Only initialize Sentry if a valid DSN is provided
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

// Defer Sentry and error-reporting to idle time or load event
function initNonCriticalServices() {
  if (SENTRY_DSN && SENTRY_DSN !== "YOUR_SENTRY_DSN") {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: process.env.NODE_ENV,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
    });
  }
  initErrorReporting();
}

// Use requestIdleCallback with correct signature, else fallback to load event
if (window.requestIdleCallback) {
  window.requestIdleCallback((_deadline) => {
    initNonCriticalServices();
  });
} else {
  window.addEventListener('load', initNonCriticalServices);
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");
const root = createRoot(rootElement);

// Use StrictMode in both development and production to ensure consistent rendering
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <Suspense fallback={<div className="loading">Loading...</div>}>
        <App />
      </Suspense>
    </HelmetProvider>
  </React.StrictMode>
);

// Unregister service worker to prevent 404 errors
serviceWorkerRegistration.unregister();

// Only enable detailed reporting in development mode
if (process.env.NODE_ENV === 'development') {
  reportWebVitals(console.log);
} else {
  reportWebVitals();
} 